import AccordionCustom from "@components/Accordion/AccordionCustom";
import ImageGrid from "@components/ImageGrid";
import { CardDataDetailsProps } from "@components/LandingPageComponents/CardDataDetails";
import FeatureCardSection from "@components/LandingPageComponents/CardSection";
import OdooHeader from "@components/LandingPageComponents/OdooHeader";
import TestimonialSection from "@components/LandingPageComponents/TestimonialSection";
import ClientLogos from "@components/new-ui/ClientLogos";
import NewLayout from "@components/new-ui/Layout";
import YellowCTAhref from "@components/yellowCTAhref";
import dynamic from "next/dynamic";
import Head from "next/head";
import goireland from "public/goireland.png";
import React from "react";

const cardDataCollection: CardDataDetailsProps[] = [
    {
        img: "https://assets.gallabox.com/gb-home/zoho-card-1.svg",
        title: "Sync Odoo Contacts with WhatsApp",
        description: "View, manage, and connect with your Odoo contacts just like any other contact right from your WhatsApp with a seamless sync between Gallabox and Odoo CRM.",
    },
    {
        img: "https://assets.gallabox.com/gb-home/personalized-card.png",
        title: "Send Automated Notifications and Alerts",
        description: "Automate the process of sending all kinds of notifications from service or purchase confirmations to payment alerts and invoices with an AI-powered chatbot.",
    },
    {
        img: "https://assets.gallabox.com/gb-home/zoho-card-3.svg",
        title: "Fetch Contact Details Without Switching Tabs",
        description:
            "Stop constantly switching tabs to fetch customer or prospect information, and retrieve all important details about a conversation or contact without leaving Gallabox.",
    },
    {
        img: "https://assets.gallabox.com/gb-home/zoho-card-4.svg",
        title: "Effortlessly Trigger Odoo Workflows Instantly",
        description:
            "Trigger your Odoo CRM workflows based on WhatsApp conversations and vice versa by enabling a two-way sync with your Odoo CRM and WhatsApp automation solution.",
    },
];

const CardDataCarousal = dynamic(() => import("@components/LandingPageComponents/CardDataCarousal"), {
    ssr: false,
});

const faqPageSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
        {
            "@type": "Question",
            name: "Can Odoo be integrated with WhatsApp?",
            acceptedAnswer: {
                "@type": "Answer",
                text: "Odoo integrates with WhatsApp to let businesses send messages to customers directly from their CRM. It's fully automated and easy to use with the ability to reach multiple contacts and send attachments. The setup and installation of the integration have specific requirements, and free support is provided for bugs or issues that arise. The Odoo-WhatsApp integration is compatible with different versions of Odoo ranging from Community to Enterprise.",
            },
        },

        {
            "@type": "Question",
            name: "How to integrate Odoo with WhatsApp Business?",
            acceptedAnswer: {
                "@type": "Answer",
                text: "Gallabox facilitates the integration of Odoo with WhatsApp businesses, allowing businesses to contact customers and prospects on WhatsApp directly from Odoo. Seamless communication with customers through WhatsApp is possible using the following steps:1. a Gallabox account and integrate your WhatsApp Business API,2.Create an account in Odoo and log in to your existing account,3.Use the Gallabox API documentation to link your WhatsApp Business account to Odoo.",
            },
        },
        {
            "@type": "Question",
            name: "How can I integrate WhatsApp Business API for free with CRM?",
            acceptedAnswer: {
                "@type": "Answer",
                text: "Integrating WhatsApp Business API for free with a CRM system is challenging but not impossible. One option is to leverage WhatsApp connectors like Zapier, which offer customizable solutions to connect with any tool. While tools like Zapier often offer free plans with limited features, they will only help meet basic integration needs. When businesses require advanced features and scalability, investing in a paid tool becomes a necessity. WhatsApp Automation tools like Gallabox provide pre-built integrations to integrate WhatsApp Business API with CRM tools like Zoho, LeadSquared, Odoo, and more.",
            },
        },
        {
            "@type": "Question",
            name: "How do I connect Odoo to WhatsApp using Gallabox?",
            acceptedAnswer: {
                "@type": "Answer",
                text: "Yes, of course, you can integrate your CRM system with WhatsApp Business API for free using a WhatsApp Automation platform like Gallabox:1.Check if your CRM provider (like Odoo) offers a free integration,2.Go to your CRM settings and find the integration or API option,3.Find the Gallabox App in your CRM marketplace,4.Install the app, enter your credentials, and log in,5.Paste your API key and voila, your WhatsApp Business API is snuggly integrated with your CRM.",
            },
        },
    ],
};

const Odoo: React.FC = () => {
    return (
        <NewLayout
            title="Odoo WhatsApp Integration for Sales & Support | Gallabox"
            ogAttributes={{ image: "https://assets.gallabox.com/gb-home/hugg/odoo-og%20(1).png" }}
            canonical="https://gallabox.com/whatsapp-integrations/odoo"
            description="Automate CRM and sales with Gallabox’s Odoo WhatsApp integration. Manage leads and engage customers directly on WhatsApp."
        >
            <Head>
                <script type="application/ld+json">{JSON.stringify(faqPageSchema)}</script>
            </Head>
            <div className="bg-white">
                <OdooHeader />

                <div className="lg:max-w-7xl mx-auto">
                    <h3 className="text-base text-center font-normal py-2">Trusted by 10,000+ businesses</h3>
                    <ClientLogos />
                </div>

                <CardDataCarousal cardDataCollection={cardDataCollection} />
                <FeatureCardSection
                    imageURl="https://assets.gallabox.com/gb-home/odoo/section1.png"
                    title="Move Everything in Your Odoo CRM into Your WhatsApp"
                    description="Stop the import-export cycle and manual errors in syncing your WhatsApp conversations with Odoo. Our meticulously crafted Odoo integration for WhatsApp imports all your contact information, customer fields, and more in just a few clicks."
                    isImageRightSide
                />

                <FeatureCardSection
                    imageURl="https://assets.gallabox.com/gb-home/odoo/section2.png"
                    title="Reach Over a Million Contacts in Just One Broadcast"
                    description="With just one click, you can send bulk messages to multiple customers, such as important product updates, seasonal promotions, and more. You can also nudge them along the sales funnel by sharing the right offers and promotions at the right time."
                />
                <FeatureCardSection
                    imageURl="https://assets.gallabox.com/gb-home/odoo/section3.png"
                    title="Nurture Leads and Increase Deals with WhatsApp Automation"
                    description="Bring all your marketing campaigns into WhatsApp to increase transparency, improve efficiency, and convert more leads. Avoid juggling between disconnected campaigns and conversations and make sure no potential deal falls between the cracks"
                    isImageRightSide
                />
                <FeatureCardSection
                    imageURl="https://assets.gallabox.com/gb-home/odoo/section4.png"
                    title="Keep All Stakeholders Informed with a Shared Team Inbox"
                    description="Access all your ongoing conversations and potential opportunities in a unified dashboard aka Gallabox’s Shared Team Inbox. Within the chat, leave private notes for employees, tag team members, assign or unassign conversations, and more."
                />
                <FeatureCardSection
                    imageURl="https://assets.gallabox.com/gb-home/odoo/section5.png"
                    title="Digitise Your Accounting Processes and Improve Accuracy"
                    description="Create, track, and manage your customer invoices and payment records with powerful integration capabilities, automated payment reminders, and real-time sync of data between your Odoo CRM and Gallabox to help you save time and maximise accuracy."
                    isImageRightSide
                />

                <YellowCTAhref
                    buttonText="Get Started Today"
                    customText={<h3 className="text-2xl md:text-3xl font-bold text-yellow-800">Sync Your Odoo Account with WhatsApp and Streamline Communication</h3>}
                />

                <TestimonialSection
                    TestimonialSectionPropsData={[
                        {
                            companyImg: goireland,
                            headerTitle: "Gallabox’s WhatsApp Widget reduced FTR  from 15-30 minutes to less than a minute, boosting engagement and conversion rates.",
                            avatarImg: "https://assets.gallabox.com/gb-home/go-ireland/goireland-owner.png",
                            nameText: "Ilaya Bharathi",
                            professionText: "Founder & CEO, GoIreland",
                        },
                    ]}
                />

                <ImageGrid />

                <YellowCTAhref
                    customText={<h3 className="text-2xl md:text-3xl font-bold text-yellow-800">Enhance Operational Efficiency with Seamless Odoo WhatsApp API Integrations</h3>}
                    buttonText="Get Started Today"
                />
                <section className="max-w-6xl py-4 mx-auto px-4">
                    <div className="flex flex-col justify-center lg:gap-4 gap-2 items-center pb-6">
                        <p className="lg:text-4xl text-3xl text-center font-bold text-gray-700">Frequently asked questions</p>
                        <h3 className="text-gray-500 text-center lg:text-2xl text-lg font-normal ">Everything you need to know about Odoo-Gallabox integration</h3>
                    </div>
                    <AccordionCustom
                        title="Odoo-landing-page"
                        items={[
                            {
                                question: "Can Odoo be integrated with WhatsApp?",
                                answer: "Odoo integrates with WhatsApp to let businesses send messages to customers directly from their CRM. It's fully automated and easy to use with the ability to reach multiple contacts and send attachments. The setup and installation of the integration have specific requirements, and free support is provided for bugs or issues that arise. The Odoo-WhatsApp integration is compatible with different versions of Odoo ranging from Community to Enterprise.",
                            },
                            {
                                question: "How to integrate Odoo with WhatsApp Business?",
                                answer: (
                                    <>
                                        <span>
                                            {
                                                "Gallabox facilitates the integration of Odoo with WhatsApp businesses, allowing businesses to contact customers and prospects on WhatsApp directly from Odoo. Seamless communication with customers through WhatsApp is possible using the following steps:"
                                            }
                                        </span>

                                        <ol className="pl-1">
                                            <li>{"Create a Gallabox account and integrate your WhatsApp Business API"}</li>
                                            <li>{"Create an account in Odoo and log in to your existing account"}</li>
                                            <li>{"Use the Gallabox API documentation to link your WhatsApp Business account to Odoo"}</li>
                                        </ol>
                                    </>
                                ),
                            },
                            {
                                question: "How can I integrate WhatsApp Business API for free with CRM?",
                                answer: (
                                    <>
                                        <span>
                                            {
                                                "Integrating WhatsApp Business API for free with a CRM system is challenging but not impossible. One option is to leverage WhatsApp connectors like Zapier, which offer customizable solutions to connect with any tool. While tools like Zapier often offer free plans with limited features, they will only help meet basic integration needs."
                                            }
                                        </span>
                                        <span>
                                            {
                                                "When businesses require advanced features and scalability, investing in a paid tool becomes a necessity. WhatsApp Automation tools like Gallabox provide pre-built integrations to integrate WhatsApp Business API with CRM tools like Zoho, LeadSquared, Odoo, and more."
                                            }
                                        </span>
                                    </>
                                ),
                            },
                            {
                                question: "How do I connect Odoo to WhatsApp using Gallabox?",
                                answer: (
                                    <>
                                        <span>
                                            {
                                                "Yes, of course, you can integrate your CRM system with WhatsApp Business API for free using a WhatsApp Automation platform like Gallabox:"
                                            }
                                        </span>

                                        <ol className="pl-1">
                                            <li>{"Check if your CRM provider (like Odoo) offers a free integration."}</li>
                                            <li>{"Go to your CRM settings and find the integration or API option."}</li>
                                            <li>{"Find the Gallabox App in your CRM marketplace."}</li>
                                            <li>{"Install the app, enter your credentials, and log in."}</li>
                                            <li>{"Paste your API key and voila, your WhatsApp Business API is snuggly integrated with your CRM."}</li>
                                        </ol>
                                    </>
                                ),
                            },
                        ]}
                    />
                </section>
            </div>
        </NewLayout>
    );
};

export default Odoo;
